import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/auth";
import {
  ArrowRight,
  BedDouble,
  BookOpenText,
  CalendarCheck,
  Globe2,
  LineChart,
  Link2,
  MessageCircleHeart,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

const HERO_CHIPS = ["AI 컨시어지", "PMS 프런트", "OTA 채널 연동", "4개 언어"];

const HERO_METRICS = [
  { value: "24시간", label: "자동 응답 컨시어지" },
  { value: "4개 언어", label: "한국어 · EN · 中文 · 日本語" },
  { value: "링크 하나", label: "게스트 앱 설치 · 가입 불필요" },
];

const FEATURES = [
  {
    icon: MessageCircleHeart,
    title: "AI 게스트 컨시어지",
    body: "게스트는 전용 링크(/g/…) 하나로 와이파이·체크인·주차·주변 정보를 채팅으로 묻습니다. 답변은 숙소가 등록한 콘텐츠만 근거로 하며, 모르는 내용은 추측 없이 프런트 문의를 안내합니다.",
  },
  {
    icon: CalendarCheck,
    title: "예약 · 프런트 (PMS)",
    body: "예약을 만들면 게스트와 챗봇 링크가 자동 발급됩니다. 체크인 시 호실 배정, 일자별 가용 현황, 점유율·매출 리포트까지 프런트 업무가 한 화면에서 끝납니다.",
  },
  {
    icon: RefreshCw,
    title: "채널 매니저 연동 (OTA)",
    body: "채널 매니저를 통해 Booking.com 등 OTA와 연결됩니다. 객실 타입·요금·재고는 자동 동기화되고, OTA 예약은 게스트 링크까지 자동 생성되어 들어옵니다.",
  },
];

const STEPS = [
  {
    icon: BedDouble,
    title: "숙소 · 객실 등록",
    body: "숙소 정보(체크인 시간·Wi-Fi·소개 문구)와 객실 타입·호실을 등록합니다. 등록 즉시 채널 매니저에 연결됩니다.",
  },
  {
    icon: BookOpenText,
    title: "가이드 · FAQ 작성",
    body: "체크인·와이파이·주차·주변 정보를 마크다운으로 등록하면 그대로 챗봇의 지식 베이스가 됩니다.",
  },
  {
    icon: Link2,
    title: "예약 등록 → 링크 전달",
    body: "예약을 만들면 게스트 전용 링크가 자동 발급됩니다. 링크를 전달하는 순간 AI 컨시어지가 응대를 시작합니다.",
  },
];

/**
 * Public landing page. Flat monochrome, type-led narrative: oversized bold
 * headline on white, pill CTAs, quiet gray sublines, generous whitespace,
 * and a near-black closing CTA band.
 */
export function LandingPage() {
  // Signed-in operators get a direct console link instead of the login CTA.
  const isAuthed = useAuthStore((s) => Boolean(s.token));
  const consoleTo = isAuthed ? "/admin" : "/admin/login";
  const consoleCta = isAuthed ? "콘솔로 이동" : "콘솔 시작하기";

  return (
    <div className="overflow-x-clip">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-6xl px-4 pb-20 pt-20 sm:px-6 md:pb-24 md:pt-32">
        <div className="animate-rise mx-auto max-w-4xl text-center">
          <p className="text-sm font-medium text-muted-foreground">
            숙소를 위한 AI 컨시어지 · PMS · 채널 연동
          </p>
          <h1 className="mt-5 break-keep font-display text-[2.75rem] font-extrabold leading-[1.06] tracking-[-0.03em] text-foreground sm:text-6xl md:text-7xl">
            게스트 응대,
            <br />
            링크 하나로 끝.
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            투숙객에게는 로그인 없는 링크 하나, 운영자에게는 예약·객실·채널까지 하나의 콘솔. 숙소
            운영의 처음과 끝을 Stayline이 잇습니다.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="w-full gap-2 sm:w-auto">
              <Link to={consoleTo}>
                {consoleCta}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
              <Link to="/guide">사용 가이드 보기</Link>
            </Button>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            {HERO_CHIPS.map((chip) => (
              <span
                key={chip}
                className="rounded-full border border-border bg-background px-3.5 py-1.5 text-xs font-medium text-muted-foreground"
              >
                {chip}
              </span>
            ))}
          </div>
        </div>

        <div className="animate-rise relative mt-16 [animation-delay:120ms]">
          <div className="overflow-hidden rounded-3xl border border-border shadow-float">
            <img
              src="/landing/hero-lobby.webp"
              alt="따뜻한 조명의 부티크 호텔 로비와 프런트 데스크"
              width={1536}
              height={1024}
              className="aspect-[3/2] w-full object-cover md:aspect-[21/9]"
            />
          </div>
          <div className="mx-auto -mt-8 grid w-[calc(100%-2rem)] max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3">
            {HERO_METRICS.map((m) => (
              <div
                key={m.value}
                className="rounded-2xl border border-border bg-card/95 px-5 py-4 text-center shadow-elevated backdrop-blur"
              >
                <p className="font-display text-xl font-bold text-foreground">{m.value}</p>
                <p className="mt-1 text-xs text-muted-foreground">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────────────────── */}
      <section id="features" className="mx-auto w-full max-w-6xl px-4 py-20 sm:px-6 md:py-24">
        <SectionHeading
          eyebrow="One Console"
          title="응대·프런트·판매 채널이 하나로."
          body="컨시어지 챗봇, PMS 프런트, OTA 채널 연동이 별도 도구가 아니라 하나의 흐름으로 이어집니다."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {FEATURES.map((f) => (
            <article
              key={f.title}
              className="group rounded-3xl border border-border bg-card p-7 transition-shadow duration-200 hover:shadow-elevated"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-foreground">
                <f.icon className="h-5 w-5" aria-hidden />
              </span>
              <h3 className="mt-5 text-lg font-bold tracking-tight text-foreground">{f.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────────── */}
      <section className="border-y border-border bg-secondary/50">
        <div className="mx-auto w-full max-w-6xl px-4 py-20 sm:px-6 md:py-24">
          <SectionHeading
            eyebrow="How it works"
            title="세 단계면 응대가 시작됩니다."
            body="아무 데이터가 없는 상태에서 게스트 응대까지, 콘솔이 순서대로 안내합니다."
          />
          <ol className="mt-12 grid gap-5 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <li key={s.title} className="relative rounded-3xl border border-border bg-card p-7">
                <div className="flex items-center gap-3">
                  <span className="font-display text-3xl font-extrabold tracking-tight text-foreground">
                    {i + 1}
                  </span>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-foreground">
                    <s.icon className="h-5 w-5" aria-hidden />
                  </span>
                </div>
                <h3 className="mt-5 text-base font-bold tracking-tight text-foreground">
                  {s.title}
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Guest experience ─────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-6xl px-4 py-20 sm:px-6 md:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading
              align="left"
              eyebrow="Guest Experience"
              title="게스트는 링크 하나로 충분합니다."
              body="앱 설치도 회원가입도 없습니다. 전달받은 링크를 열면 선택한 언어로 환영 화면과 챗봇이 준비됩니다."
            />
            <ul className="mt-8 space-y-4">
              <LandingCheckItem icon={Globe2}>
                한국어 · English · 中文 · 日本語 — 환영 문구, 빠른 질문 버튼, 답변까지 자동 번역
              </LandingCheckItem>
              <LandingCheckItem icon={Sparkles}>
                체크인 일정, 객실 안내, 소개 이미지가 담긴 개인화된 환영 화면
              </LandingCheckItem>
              <LandingCheckItem icon={MessageCircleHeart}>
                표·목록으로 정리된 답변, 자주 묻는 질문은 버튼 한 번으로
              </LandingCheckItem>
            </ul>
          </div>
          <div className="relative">
            <div className="overflow-hidden rounded-3xl border border-border shadow-float">
              <img
                src="/landing/guest-chat.webp"
                alt="침대에 기대 스마트폰으로 컨시어지와 대화하는 투숙객"
                width={1536}
                height={1024}
                className="aspect-[3/2] w-full object-cover"
                loading="lazy"
              />
            </div>
            <img
              src="/guides/guest-03-conversation.webp"
              alt="게스트 채팅 화면 — 표로 정리된 챗봇 답변"
              width={320}
              height={640}
              className="absolute -bottom-8 -left-4 hidden w-40 rotate-[-3deg] rounded-2xl border border-border bg-card shadow-float sm:block md:w-48"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* ── Console preview ──────────────────────────────────────────────── */}
      <section className="border-y border-border bg-secondary/50">
        <div className="mx-auto w-full max-w-6xl px-4 py-20 sm:px-6 md:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
              <div className="overflow-hidden rounded-3xl border border-border shadow-float">
                <img
                  src="/guides/admin-22-dashboard-filled.webp"
                  alt="운영자 콘솔 대시보드 — 게스트·가이드 현황"
                  width={1440}
                  height={900}
                  className="w-full"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <SectionHeading
                align="left"
                eyebrow="Operator Console"
                title="숙소가 몇 개여도, 콘솔은 하나."
                body="계정 하나로 여러 숙소를 등록하고 사이드바에서 전환합니다. 예약·호실·가이드·리포트가 모두 선택한 숙소 기준으로 움직입니다."
              />
              <ul className="mt-8 space-y-4">
                <LandingCheckItem icon={CalendarCheck}>
                  예약 생성 → 게스트 링크 자동 발급, 체크인 시 호실 배정
                </LandingCheckItem>
                <LandingCheckItem icon={RefreshCw}>
                  객실 타입·요금·재고는 채널로 자동 동기화, OTA 예약은 자동 수신
                </LandingCheckItem>
                <LandingCheckItem icon={LineChart}>
                  기간별 점유율 · 총 매출 · 채널별 매출 리포트
                </LandingCheckItem>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-6xl px-4 py-20 sm:px-6 md:py-28">
        <div className="rounded-3xl bg-foreground px-6 py-16 text-center text-background sm:px-12 sm:py-20">
          <p className="text-sm font-medium text-background/60">Start today</p>
          <h2 className="mx-auto mt-4 max-w-2xl break-keep font-display text-3xl font-extrabold leading-tight tracking-[-0.03em] sm:text-5xl">
            오늘 등록한 숙소가
            <br />
            오늘 밤 게스트를 맞이합니다.
          </h2>
          <p className="mx-auto mt-5 max-w-lg text-sm text-background/70 sm:text-base">
            설정은 콘솔이 순서대로 안내합니다. 막히는 부분은 사용 가이드와 FAQ를 참고하세요.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              asChild
              size="lg"
              className="gap-2 bg-background text-foreground hover:bg-background/90"
            >
              <Link to={consoleTo}>
                {consoleCta}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-background/30 bg-transparent text-background hover:bg-background/10 hover:text-background"
            >
              <Link to="/faq">자주 묻는 질문</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionHeading({
  eyebrow,
  title,
  body,
  align = "center",
}: {
  eyebrow: string;
  title: string;
  body: string;
  align?: "center" | "left";
}) {
  return (
    <div className={align === "center" ? "mx-auto max-w-2xl text-center" : "max-w-xl"}>
      <p className="eyebrow">{eyebrow}</p>
      <h2 className="mt-3 break-keep font-display text-3xl font-extrabold tracking-[-0.03em] text-foreground sm:text-4xl">
        {title}
      </h2>
      <p className="mt-4 text-base leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

function LandingCheckItem({
  icon: Icon,
  children,
}: {
  icon: typeof Globe2;
  children: ReactNode;
}) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-foreground">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <span className="text-sm leading-relaxed text-foreground/85">{children}</span>
    </li>
  );
}
